import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, FormControl, Grid, MenuItem, Select, Stack, Typography } from '@mui/material';
import KpiComponent from '@/components/Base/KpiComponent';
import FirstAccessModal from '@/components/Base/FirstAccessModal';
import { useConsolidationSummary } from '@/features/dashboard/api/getConsolidationSummary.ts';
import LoadingSummary from '@/features/dashboard/components/LoadingSummary.tsx';
import CurrencyFormat from '@/utils/CurrencyFormat.ts';
import ScoreInadimplencia from '@/features/dashboard/ScoreInadimplencia.tsx';
import PedagogicalHistory from '@/features/dashboard/PedagogicalHistory';
import ScoreFinanceiroGeral from '@/features/dashboard/ScoreFinanceiroGeral.tsx';
import ScoreHistoricoFinanceiro from '@/features/dashboard/ScoreHistoricoFinanceiro.tsx';
import CashFlow from '@/features/dashboard/CashFlow';
import Frequency from '@/features/dashboard/Frequency';
import ScoreMovimentacaoEscolar from '@/features/dashboard/ScoreMovimentacaoEscolar.tsx';
import ScoreGeralAlunos from '@/features/dashboard/ScoreGeralAlunos';
import { TitlePages } from '@/components/Base/TitlePage';
import { useConsolidationScoreGeralAlunos } from '@/features/dashboard/api/getConsolidationScoreGeralAlunos';
import { groupBy } from 'lodash';
import ChartCashFlow from '@/components/Base/ChartCashFlow';
import { useFluxoCaixaGraficos } from '@/features/fluxo-caixa/api/getFluxoCaixaGraficos';

const currentYear = new Date().getFullYear();
const availableYears = Array.from({ length: 6 }, (_, i) => currentYear - i);

export const Dashboard = () => {
  const [open, setOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const { data: summary, isError, refetch, isFetching } = useConsolidationSummary(selectedYear);
  const { data: scoreGeral, isFetched } = useConsolidationScoreGeralAlunos(selectedYear);

  const [generalFilter, setGeneralFilter] = useState(false);
    const {
      data: selectedGraficos,
      isFetched: isFetchedGraficos,
      isFetching: isFetchingGraficos,
    } = useFluxoCaixaGraficos({ year: selectedYear, ...( generalFilter ? {} : {}) });
  


  useEffect(() => {
    const openEduModal = localStorage.getItem('openModalEdu');

    if (!openEduModal) {
      setOpen(true);
      localStorage.setItem('openModalEdu', 'true');
    }
  }, []);
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <TitlePages title={'Dashboard EduQuanto'} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ color: '#000', backgroundColor: '#fff', borderRadius: '8px' }}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box
        padding={'15px'}
        sx={{
          border: '1px solid rgba(176, 176, 176, 1)',
          background: '#fff',
          borderRadius: '14px',
        }}
      >
        {
          isError && (
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={() => {
                refetch();
              }}>
                Atualizar
              </Button>
            }>Não foi possível carregar as informações!</Alert>
          )
        }

        {
          summary && (
            <Stack flexDirection={'row'} gap={'8px'}>
              <Stack
                flexDirection="row"
                maxWidth="100%"
                gap="8px"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#E04A4C #f1f1f1',
                }}
              >
                <KpiComponent title={'Faturamento Acumulado'} value={CurrencyFormat.format(summary.fatumranento_acumulado)} sign tooltipText='O Faturamento Acumulado é retirado diretamente do relatório de faturamento'/>
                <KpiComponent title={'Receita Bruta (mês atual)'} value={CurrencyFormat.format(summary.faturamento_bruto)} sign tooltipText='O Faturamento Bruto é retirado diretamente do relatório de faturamento' />
                <KpiComponent title={'Receita mensal líquida (mês atual)'} value={CurrencyFormat.format(summary.receita_mensal_liquida)} sign tooltipText='A Receita mensal líquida é retirada diretamente do relatório de faturamento' />
                <KpiComponent title={'Ticket médio líquido (mês atual)'} value={CurrencyFormat.format(summary.ticket_medio)} sign tooltipText='O Ticket médio líquido é retirado diretamente do relatório de faturamento' />
                <KpiComponent title={'Desconto total (mês atual)'} value={CurrencyFormat.format(summary.desconto_total)} sign tooltipText='O Desconto total é retirado diretamente do relatório de faturamento' />
                <KpiComponent
                  status={summary.media_desconto?.state}
                  title={'Média de descontos (mês atual)'}
                  value={`${CurrencyFormat.formatWithDecimais(summary.media_desconto?.medium) || '-'}%`} 
                  percentage={summary.media_desconto?.percentage_value > 0 && `${summary.media_desconto?.percentage_value?.toFixed(2)}`}
                  tooltipText='A Média de descontos é retirada diretamente do relatório de faturamento'
                />

                <KpiComponent
                  status={summary.score_geral?.state}
                  title={'Score geral'}
                  value={String(scoreGeral?.length > 0 ? scoreGeral[scoreGeral.length - 1]?.valor : 0).replace('.', ',')}
                  percentage={summary?.score_geral?.percentage_value > 0 && `${summary?.score_geral?.percentage_value}%`} 
                  tooltipText={
                    <div>
                      <strong>Score Geral:</strong> é anual e calculado como a média dos scores Financeiro, Pedagógico e Frequência.
                      <br /><br />
                      <strong>Score Financeiro:</strong> Calculado como a média entre o valor total pago dentro do prazo de vencimento e o valor total previsto em boletos em cada mês.
                      <br /><br />
                      <strong>Score Pedagógico:</strong> Calculado como a média das médias das notas de cada aluno. A média das notas é obtida pela soma das notas em proporção à quantidade de disciplinas.
                      <br /><br />
                      <strong>Score Frequência:</strong> Calculado como a média das frequências individuais dos alunos. A frequência individual é a quantidade de presenças dividida pela quantidade total de aulas.
                    </div>
                  }
                />


                <KpiComponent
                  title={'LT: Médio'} value={`${(summary.lifetime)} anos`} tooltipText={'Calcula o tempo médio de permanência dos alunos ativos, com base na diferença entre o primeiro e o último período letivo registrados para cada aluno. (O calculo também considera alunos inativos)'} />

                <KpiComponent
                  title={'LTV: Médio'} value={`${CurrencyFormat.format(summary.lifetimevalue)}`} tooltipText={'Calcula o valor médio (LTV) pago por aluno ativo, considerando a soma de todos os boletos pagos por cada aluno. (O calculo também considera alunos inativos)'}  /* value={"7 anos"}  *//>
                
                <KpiComponent
                  title={'Alunos ativos'} value={summary.active_students || '0'} />
                
                <KpiComponent
                  title={'Inadimplência'} value={`${CurrencyFormat.formatWithDecimais(summary.inadimplence?.medium) || '0'}%`} /* value={"3,5%"} */
                  status={summary?.inadimplence?.state}
                  percentage={summary?.inadimplence?.percentage_value || 0}
                  tooltipText={'Relaciona Total a receber no ano e Total de boletos atrasados no ano corrente.'}
                  sign
                />
                <KpiComponent title={'Valor da inadimplência acumulada'} value={CurrencyFormat.format(summary.accumulated_inadimplence_value)} sign status={'down'} tooltipText={'Calcula o valor total inadimplente do ano atual, somando todos os boletos vencidos e não pagos até a data atual.'}/>
                
                <KpiComponent title={'Ocupação das instituições'} value={`${summary.occupationPercentage}%`} status={'down'} />

                <KpiComponent title={'Índice de valor médio'} value={CurrencyFormat.formatWithDecimais(summary.average_index_value?.percentage_value) + '%'} /*value={"5%"} */ status={'down'} tooltipText={'Corresponde à média, no ano atual, dos índices calculados para cada série. Cada índice representa a variação percentual dos valores de mensalidade da série ao longo dos últimos anos, ajustada pela inflação do ano anterior ao seu período.'}/>
              </Stack>
            </Stack>
          )
        }

        {isFetching && !summary && (<LoadingSummary />)}

        <FirstAccessModal open={open} handleClose={handleClose} />

        <Grid mt={'10px'} container spacing={2}>
          <Grid item xs={6}>
            <ScoreInadimplencia year={selectedYear} />
          </Grid>
          <Grid item xs={6}>
            <PedagogicalHistory year={selectedYear} />
          </Grid>
        </Grid>

        <Grid mt={'10px'} container spacing={2}>
          <Grid item xs={6}>
            <ScoreFinanceiroGeral year={selectedYear} />
          </Grid>
          <Grid item xs={6}>
            <Frequency year={selectedYear} />
          </Grid>
        </Grid>

        <Grid mt={'10px'} container spacing={2}>
          <Grid item xs={6}>
          <ChartCashFlow showFilter={true} title={'Evolução do saldo'} data={selectedGraficos?.balance_evolution || []}  setFilter={setGeneralFilter} filter={generalFilter}/>

          </Grid>
          <Grid item xs={6}>
            <ScoreGeralAlunos year={selectedYear} />
          </Grid>
        </Grid>


        <Grid mt={'10px'} mb={'20px'} container spacing={2}>
          <Grid item xs={6}>
            <ScoreHistoricoFinanceiro year={selectedYear} />
          </Grid>
          <Grid item xs={6}>
            <ScoreMovimentacaoEscolar year={selectedYear} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
